import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

export default function TeamManagement({ developerId }) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('admin')
  const [error, setError] = useState(null)

  const { data: teamData, isLoading: teamLoading } = useQuery({
    queryKey: ['team-members', developerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('developer_team_members')
        .select(`
          *,
          member:member_id (
            id,
            full_name,
            email,
            role
          )
        `)
        .eq('developer_id', developerId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })

  const { data: invitationsData } = useQuery({
    queryKey: ['team-invitations', developerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('developer_id', developerId)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })

  const inviteMutation = useMutation({
    mutationFn: async ({ email, role }) => {
      const token = crypto.randomUUID() + crypto.randomUUID()

      const { data, error } = await supabase
        .from('team_invitations')
        .insert({
          developer_id: developerId,
          email,
          role,
          token,
          invited_by: user.id,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['team-invitations', developerId])
      setShowInviteForm(false)
      setInviteEmail('')
      setInviteRole('admin')
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to send invitation')
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId) => {
      const { error } = await supabase
        .from('developer_team_members')
        .delete()
        .eq('developer_id', developerId)
        .eq('member_id', memberId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['team-members', developerId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to remove team member')
    },
  })

  const revokeInviteMutation = useMutation({
    mutationFn: async (inviteId) => {
      const { error } = await supabase
        .from('team_invitations')
        .update({ status: 'revoked' })
        .eq('id', inviteId)
        .eq('developer_id', developerId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['team-invitations', developerId])
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Failed to revoke invitation')
    },
  })

  const handleInviteSubmit = (e) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole })
  }

  const handleRemoveMember = (memberId, memberName) => {
    if (!confirm(`Remove ${memberName} from the team?`)) return
    removeMemberMutation.mutate(memberId)
  }

  const handleRevokeInvite = (inviteId, email) => {
    if (!confirm(`Revoke invitation for ${email}?`)) return
    revokeInviteMutation.mutate(inviteId)
  }

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'admin':
        return 'bg-blue-100 text-blue-800'
      case 'sales':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-secondary-100 text-secondary-800'
    }
  }

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  const teamMembers = teamData || []
  const pendingInvites = invitationsData || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Team Members</h3>
          <p className="text-sm text-secondary-600">
            Manage your developer team and permissions
          </p>
        </div>
        <button
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="btn-primary px-4 py-2"
        >
          {showInviteForm ? 'Cancel' : 'Invite Team Member'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {showInviteForm && (
        <form onSubmit={handleInviteSubmit} className="card p-6 bg-secondary-50">
          <h4 className="text-md font-semibold text-secondary-900 mb-4">Invite New Team Member</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="label">Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="input"
                placeholder="colleague@example.com"
                required
              />
            </div>
            <div>
              <label className="label">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="input"
              >
                <option value="admin">Admin</option>
                <option value="sales">Sales</option>
              </select>
              <p className="text-xs text-secondary-600 mt-1">
                Admin: Full project access. Sales: Buyer communication only
              </p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => setShowInviteForm(false)}
              className="btn-outline flex-1 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="btn-primary flex-1 py-2"
            >
              {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      )}

      {pendingInvites.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 bg-secondary-50 border-b border-secondary-200">
            <h4 className="font-medium text-secondary-900">Pending Invitations</h4>
          </div>
          <div className="divide-y divide-secondary-200">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900">{invite.email}</p>
                  <p className="text-xs text-secondary-600">
                    Invited {new Date(invite.created_at).toLocaleDateString()} •
                    Expires {new Date(invite.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeClass(invite.role)}`}>
                    {invite.role}
                  </span>
                  <button
                    onClick={() => handleRevokeInvite(invite.id, invite.email)}
                    className="text-sm text-red-600 hover:text-red-700"
                    disabled={revokeInviteMutation.isPending}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {teamMembers.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-secondary-400 mb-3">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-secondary-900 mb-1">No team members yet</h4>
          <p className="text-secondary-600">
            Invite team members to help manage your projects
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {teamMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                      {member.member?.full_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-700">
                      {member.member?.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleBadgeClass(member.role)}`}>
                        {member.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.member_id, member.member?.full_name)}
                          className="text-red-600 hover:text-red-900"
                          disabled={removeMemberMutation.isPending}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-6 bg-blue-50 border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Team Roles</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Owner:</strong> Full access to all projects and team management</li>
          <li><strong>Admin:</strong> Can manage projects, units, pricing, and discounts</li>
          <li><strong>Sales:</strong> Can only communicate with buyers and view project details</li>
        </ul>
      </div>
    </div>
  )
}
